import { CognitoUser } from "@aws-amplify/auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Auth } from "aws-amplify";
import * as localStorageKeys from "data/local-storage-keys";
import { ReactQueryMutationError } from "data/mutations-common";
import { CheckCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { ChangeEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

interface MfaSetupProps {
  onSubmit: () => void;
  onOptOut: () => void;
  cognitoUser: CognitoUser;
  style?: React.CSSProperties;
  startFromStep?: number;
  createUserOrg?: () => void;
}

export const MfaSetupOnboarding = (props: MfaSetupProps) => {
  const [user] = useState<CognitoUser | null>(props.cognitoUser);
  const [qrCode, setQrCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [step, setStep] = useState(props.startFromStep ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsVerifyCode, setSmsVerifyCode] = useState("");

  const setupTotpMutation = useMutation({
    mutationFn: () => Auth.setupTOTP(user),
  });
  const verifyTotpTokenMutation = useMutation({
    mutationFn: () => Auth.verifyTotpToken(user, mfaCode),
    onSuccess: async () => {
      await setPreferredMFAMutation.mutateAsync();
      setQrCode("");
      setStep(3);
    },
  });
  const setPreferredMFAMutation = useMutation({
    mutationFn: () => Auth.setPreferredMFA(user, "TOTP"),
  });
  const setupSMSMutation = useMutation({
    mutationFn: async () => {
      await Auth.updateUserAttributes(user, { phone_number: phoneNumber });
      Auth.verifyCurrentUserAttribute("phone_number");
    },
  });
  const verifySMSMutation = useMutation({
    mutationFn: () =>
      Auth.verifyCurrentUserAttributeSubmit("phone_number", smsVerifyCode).then(
        () => Auth.setPreferredMFA(user, "SMS_MFA"),
      ),
    onSuccess: () => setStep(3),
  });

  const onPhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const regex = /^\+(\d{0,11})$/;
    if (regex.test(value) || value === "") setPhoneNumber(value);
  };

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMfaCode(event.target.value.replace(/[^0-9]/g, ""));
  };

  const onSmsCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSmsVerifyCode(event.target.value.replace(/[^0-9]/g, ""));
  };

  useEffect(() => {
    const setupTOTP = async () => {
      const code = await setupTotpMutation.mutateAsync();
      setQrCode(
        `otpauth://totp/Geoscape Hub:${user?.getUsername()}?secret=${code}&issuer=Geoscape Hub`,
      );
    };
    setupTOTP();
  }, []);

  const verifyTOTP = async () => {
    verifyTotpTokenMutation.mutateAsync();
  };

  return (
    <>
      {step == 0 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>Setup Multi Factor Authentication</CardTitle>
            <CardDescription>
              Geoscape recommends you protect your account with MFA (Multi
              Factor Authentication).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>
                MFA is optional but recommended for security.
              </AlertDescription>
            </Alert>
            <div className="flex justify-end gap-2">
              <Button
                id="optOutMfa"
                variant="outline"
                onClick={() => setStep(-1)}
              >
                OPT OUT
              </Button>
              <Button onClick={() => setStep(1)}>Setup MFA</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step == 1 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>MFA Method</CardTitle>
            <CardDescription>
              Geoscape supports two methods of MFA. You'll only be able to use
              one method at a time. We highly recommend TOTP.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button onClick={() => setStep(2)}>
              Authenticator App (TOTP)
            </Button>
            <Button onClick={() => setStep(-2)}>Text Messaging (SMS)</Button>
            <Button variant="outline" onClick={() => setStep(0)}>
              Back
            </Button>
          </CardContent>
        </Card>
      )}

      {step == -1 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>Opt out of MFA?</CardTitle>
            <CardDescription>
              Geoscape recommends you protect your account with MFA. Are you
              sure you want to opt out?
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>
                You will be able to configure MFA at a later date from inside
                your account settings.
              </AlertDescription>
            </Alert>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button
                id="confirmOptOutMfa"
                disabled={isLoading}
                onClick={() => {
                  setIsLoading(true);
                  localStorage.setItem(localStorageKeys.optOutMfa, "true");
                  props.onOptOut();
                }}
              >
                {isLoading ? <Spinner /> : "Confirm OPT-OUT"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step == 2 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>Setup Multi Factor Authentication</CardTitle>
            <CardDescription>
              Use your authenticator app to scan the QR code displayed below
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {setupTotpMutation.isPending && (
              <Skeleton className="size-[225px] self-center" />
            )}
            {setupTotpMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  An error occured when trying to generate QR code
                </AlertDescription>
              </Alert>
            )}
            {setupTotpMutation.isSuccess && !!qrCode && (
              <>
                <div className="self-center">
                  <QRCodeSVG value={qrCode} size={225} />
                </div>
                {verifyTotpTokenMutation.isError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {
                        (
                          verifyTotpTokenMutation.error as ReactQueryMutationError
                        ).message
                      }
                    </AlertDescription>
                  </Alert>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="totpCode">TOTP Code</Label>
                  <Input
                    id="totpCode"
                    maxLength={6}
                    disabled={
                      setupTotpMutation.isPending ||
                      verifyTotpTokenMutation.isPending ||
                      setPreferredMFAMutation.isPending
                    }
                    value={mfaCode}
                    onChange={onCodeChange}
                    onKeyDown={async (ev) => {
                      if (ev.key === "Enter" && mfaCode.length === 6)
                        await verifyTOTP();
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              {setupTotpMutation.isSuccess && (
                <Button
                  disabled={
                    setupTotpMutation.isPending || mfaCode.length !== 6
                  }
                  onClick={async () => await verifyTOTP()}
                >
                  {verifyTotpTokenMutation.isPending ||
                  setPreferredMFAMutation.isPending ? (
                    <Spinner />
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {step == -2 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>Setup SMS MFA</CardTitle>
            <CardDescription>
              We'll text a verification code to this mobile device whenever you
              sign in to Geoscape Hub.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>
                Standard SMS charges may apply from your operator.
              </AlertDescription>
            </Alert>
            <div className="space-y-1.5">
              <Label htmlFor="phoneNumber">Mobile Number</Label>
              <Input
                id="phoneNumber"
                required
                maxLength={12}
                placeholder="+61xxxxxxxxx"
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                onKeyDown={async (ev) => {
                  if (ev.key === "Enter" && phoneNumber.length === 12)
                    await setupSMSMutation.mutateAsync();
                }}
              />
              <p className="text-xs text-muted-foreground">
                By providing your mobile number, you agree to receive text
                messages from Geoscape.
              </p>
            </div>
            <Button
              disabled={
                setupSMSMutation.isPending ||
                !phoneNumber ||
                phoneNumber.length !== 12
              }
              onClick={async () => await setupSMSMutation.mutateAsync()}
            >
              {setupSMSMutation.isPending ? <Spinner /> : "Send Code"}
            </Button>
            <div className="space-y-1.5">
              <Label htmlFor="smsCode">Verification Code</Label>
              <Input
                id="smsCode"
                maxLength={6}
                value={smsVerifyCode}
                onChange={onSmsCodeChange}
                onKeyDown={async (ev) => {
                  if (ev.key === "Enter" && smsVerifyCode.length === 6)
                    await verifySMSMutation.mutateAsync();
                }}
              />
            </div>
            {verifySMSMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {(verifySMSMutation.error as ReactQueryMutationError).message}
                </AlertDescription>
              </Alert>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                disabled={
                  verifySMSMutation.isPending ||
                  !smsVerifyCode ||
                  smsVerifyCode.length !== 6
                }
                onClick={async () => await verifySMSMutation.mutateAsync()}
              >
                {verifySMSMutation.isPending ? <Spinner /> : "Submit"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card className="w-[436px]">
          <CardHeader>
            <CardTitle>Success!</CardTitle>
            <CardDescription>
              MFA has successfully been setup for your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 items-center">
            <CheckCircle className="size-10 text-success" />
            <Alert>
              <AlertDescription>
                You can manage your MFA settings anytime by going to Settings
                &gt; General
              </AlertDescription>
            </Alert>
            <Button
              className="w-full"
              disabled={isLoading}
              onClick={() => {
                setIsLoading(true);
                props.onSubmit();
              }}
            >
              {isLoading ? <Spinner /> : "Done"}
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );
};
